import { Component, type ErrorInfo, type ReactNode } from "react";

import { reportFrontendError } from "./errors";

/*
 * Cross-SPA render-error boundary. Every Actionary browser surface
 * mounts one of these near its root — admin, portal, marketing, widget
 * — so a render failure on any surface reports through the shared
 * `/platform/frontend-errors` pipe and renders the same recovery
 * affordance ("Something went wrong. Our team has been notified.").
 *
 * The shell is style-agnostic — surfaces pass their own class names
 * for the outer container, card, heading, paragraph, detail block,
 * and action row so the recovery UI matches each surface's design
 * vocabulary (admin uses shadcn's bg-background + bg-card; portal
 * uses slate-50 + slate-900). Substrate handles the render-error
 * capture, reporting, and copy; surfaces choose the shell.
 *
 * `actions` is the surface's recovery-button strip — a fully rendered
 * ReactNode the substrate slots into the action row. Every surface
 * composes its own buttons (Reload, Go home, and any surface-specific
 * extras like admin's "Open system alerts") so button styling stays
 * inside the surface's design system rather than being reinvented
 * here.
 *
 * `showDetails` opts a surface into rendering the raw error message
 * inline — admin does this on the staff origin so operators can
 * eyeball the failure without leaving the page; public-facing
 * surfaces leave it false so visitors never see stack noise.
 */

interface State {
  hasError: boolean;
  message: string;
}

export type FrontendErrorBoundaryClassNames = {
  container?: string;
  card?: string;
  heading?: string;
  paragraph?: string;
  detail?: string;
  actionRow?: string;
};

export type FrontendErrorBoundaryProps = {
  children: ReactNode;
  /**
   * Fully-rendered recovery-button strip. Every surface composes its
   * own so button styling lives in the surface's design system, not
   * the substrate.
   */
  actions: ReactNode;
  /** Render the raw error message inline (staff surfaces only). */
  showDetails?: boolean;
  /**
   * Style hooks. Each surface passes its own class-name shape so the
   * recovery UI paints in its design vocabulary. Substrate uses
   * empty-string defaults so the boundary is style-safe without them.
   */
  classNames?: FrontendErrorBoundaryClassNames;
};

const DEFAULT_HEADING = "Something went wrong.";
const DEFAULT_BODY =
  "The page hit an unexpected error and stopped rendering. Your work so far is safe — reload to recover, or step back to the home page. Our team has been notified automatically.";

export class FrontendErrorBoundary extends Component<
  FrontendErrorBoundaryProps,
  State
> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : String(error),
    };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportFrontendError({
      source: "react_render",
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack ?? null : null,
      details: {
        component_stack: info?.componentStack ?? null,
      },
    });
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    const cn = this.props.classNames ?? {};
    return (
      <div className={cn.container ?? ""}>
        <div className={cn.card ?? ""}>
          <h1 className={cn.heading ?? ""}>{DEFAULT_HEADING}</h1>
          <p className={cn.paragraph ?? ""}>{DEFAULT_BODY}</p>
          {this.props.showDetails ? (
            <pre className={cn.detail ?? ""}>{this.state.message}</pre>
          ) : null}
          <div className={cn.actionRow ?? ""}>{this.props.actions}</div>
        </div>
      </div>
    );
  }
}
